import { Form, Formik } from 'formik';
import { useMemo } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { CardWithTitle } from '@hivebrite/design-system/Card';

import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import Navigation from 'hb-react/shared/apps/Events/pages/Booking/components/Navigation';
import { PromoCode } from 'hb-react/shared/apps/Events/pages/Booking/components/PromoCode';
import { ReCaptcha } from 'hb-react/shared/apps/Events/pages/Booking/components/ReCaptcha';
import { useBooking } from 'hb-react/shared/apps/Events/pages/Booking/hooks/useBooking';
import ErrorFocus from 'hb-react/shared/components/formik/components/ErrorFocus';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import { goToPaymentPage } from 'hb-react/shared/utils/paymentPage';

import { BackButton } from './BackButton';
import BillingAndShippingAddress from './BillingAndShippingAddress';
import FreeButton from './FreeButton';
import PayButtons from './PayButtons';
import PersonalInformation from './PersonalInformation';
import ShippingAddressForm from './ShippingAddress';
import { getInitialValues } from './utils';

export const BillingInformationPage = () => {
  const { cart, event } = useBooking();

  if (!cart || !event) {
    return null;
  }

  return <BillingInformation />;
};

const BillingInformation = () => {
  const navigate = useNavigate();
  const topicContext = useTopicContext();

  const { flagEvPaymentsMovePromocode, flagEvPaymentPageEvents } =
    useFeatureFlag();
  const {
    currentNetwork: { user_fields_config: userFieldsConfig },
    currentUser,
  } = useDefaultAppContexts();

  const { cart, event, actions, metadata } = useBooking();

  const isShippable = !!cart?.cart_items?.find(
    ({ quantity, is_shippable: isShippable }) => quantity > 0 && isShippable,
  );

  const eligibleForPromocodes =
    !flagEvPaymentsMovePromocode && metadata?.eligible_for_promocodes;

  const totalPrice = cart?.total_in_cents || 0;

  const shouldDisplayBillingSection = totalPrice > 0;

  const initialValues = useMemo(
    () =>
      getInitialValues({
        currentUser,
        metadata,
        isShippable,
      }),
    [currentUser, isShippable, metadata],
  );

  if (!cart || !event) {
    return null;
  }

  // eslint-disable-next-line camelcase
  const onSubmit = async (
    { receipt, captcha_response: captchaResponse },
    { setErrors },
  ) => {
    try {
      const response = await actions.reset({
        receipt,
      });

      if (!response) {
        return;
      }

      const { data } = response;

      if (data.cart.total_in_cents > 0) {
        if (flagEvPaymentPageEvents) {
          goToPaymentPage({
            cartId: cart.id,
            topicContext,
            captchaResponse,
          });

          return;
        }

        return navigate('../payment');
      }

      // eslint-disable-next-line camelcase
      await actions.complete({ captcha_response: captchaResponse });

      navigate('../success');
    } catch (e) {
      if (e.response?.data?.error_code === 'INCORRECT_CAPTCHA') {
        setErrors({ captcha_response: e.response.data.errors });
      }
    }
  };

  const handlePayLater = async (formikHelpers) => {
    const { validateForm, setFieldTouched, setFieldError, values } =
      formikHelpers;
    const errors = await validateForm();

    if (Object.keys(errors).length === 0) {
      await actions.payLater(values);
      navigate('../success');

      return;
    }

    // Mark fields as touched to display error messages
    // We need to do this before setting errors because Formik only shows errors
    // for fields that are either touched or the form has been submitted
    const fieldsWithErrors = Object.keys(
      errors.receipt.billing_address_attributes,
    );

    fieldsWithErrors.forEach((field) => {
      const fieldPath = `receipt[billing_address_attributes][${field}]`;

      setFieldTouched(fieldPath, true);
      setFieldError(
        fieldPath,
        errors.receipt.billing_address_attributes[field],
      );
    });
  };

  return (
    <>
      <Outlet />
      <Box display="block" data-testid="event-registration-form-billing">
        <Formik initialValues={initialValues} onSubmit={onSubmit}>
          {({
            isSubmitting,
            submitForm,
            isValid,
            values,
            validateForm,
            setFieldTouched,
            setFieldError,
          }) => (
            <Form
              style={{
                rowGap: '24px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <PersonalInformation userFieldsConfig={userFieldsConfig} />
              {shouldDisplayBillingSection && (
                <BillingAndShippingAddress
                  userFieldsConfig={userFieldsConfig}
                  isShippable={isShippable}
                />
              )}

              {isShippable && !shouldDisplayBillingSection && (
                <CardWithTitle
                  title={s__('NetworkEvents|Enter shipping address details')}
                  display="block"
                >
                  <ShippingAddressForm userFieldsConfig={userFieldsConfig} />
                </CardWithTitle>
              )}

              {eligibleForPromocodes && (
                <CardWithTitle
                  title={s__('Events|Promo code')}
                  mb={5}
                  pt={4}
                  pb={2}
                >
                  <PromoCode />
                </CardWithTitle>
              )}
              <ReCaptcha />

              <Navigation>
                {({ isFixed, breakpoint }) => (
                  <Box width="100%" justifyContent="space-between">
                    <LeftButtonsWrapper>
                      <BackButton isFixed={isFixed} />
                    </LeftButtonsWrapper>
                    <Box pl={2} alignItems="flex-start">
                      {totalPrice > 0 && (
                        <PayButtons
                          isValid={isValid}
                          isFixed={isFixed}
                          breakpoint={breakpoint}
                          event={event}
                          isSubmitting={isSubmitting}
                          submitForm={submitForm}
                          onPayLater={() =>
                            handlePayLater({
                              validateForm,
                              setFieldTouched,
                              setFieldError,
                              values,
                            })
                          }
                        />
                      )}

                      {totalPrice === 0 && (
                        <FreeButton
                          isSubmitting={isSubmitting}
                          submitForm={submitForm}
                          isValid={isValid}
                        />
                      )}
                    </Box>
                  </Box>
                )}
              </Navigation>
              <ErrorFocus />
            </Form>
          )}
        </Formik>
      </Box>
    </>
  );
};

const LeftButtonsWrapper = styled(Box).attrs({
  alignItems: 'flex-start',
  flexDirection: { _: 'column', m: 'unset' },
  width: { m: 'inherit' },
  justifyContent: 'space-between',
})``;
